import { useState } from 'react'
import { MessageSquare, Plus } from 'lucide-react'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { AdminGestionDialog } from '../components/AdminGestionDialog'
import { ConsumoMetaFiltersBar } from '../components/ConsumoMetaFiltersBar'
import { ConsumoMetaTable } from '../components/ConsumoMetaTable'
import { useConsumoMeta, useCreateConsumoMeta } from '../hooks/useConsumoMeta'
import type { ConsumoMetaFilters } from '../types/consumoMeta'

export function ConsumoMetaPage() {
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.groups?.includes('admin') || false

  const [filters, setFilters] = useState<ConsumoMetaFilters>({
    anio: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
  })
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data: registros = [], isLoading } = useConsumoMeta(filters)

  const filtersBase = { anio: filters.anio, mes: filters.mes }
  const { data: registrosBase = [] } = useConsumoMeta(filtersBase)
  const paisesDisponibles = [...new Set(registrosBase.map((r) => r.pais))]

  const createMutation = useCreateConsumoMeta()

  const handleSubmit = async (data: any) => {
    await createMutation.mutateAsync(data)
    setDialogOpen(false)
  }

  const totalEnvios = registros.reduce((acc, r) => acc + r.total_envio, 0)
  const totalDolares = registros.reduce((acc, r) => acc + r.total_valor, 0)
  const totalMarketing = registros.filter((r) => r.tipo_envio === 'marketing').reduce((acc, r) => acc + r.total_envio, 0)
  const totalUtilidad = registros.filter((r) => r.tipo_envio === 'utilidad').reduce((acc, r) => acc + r.total_envio, 0)

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(n)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-emerald-500/10 p-2">
            <MessageSquare className="h-6 w-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-black dark:text-white">Consumo Meta</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Control de envíos por laboratorio
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo registro
          </Button>
        )}
      </div>

      {registros.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total envíos', value: totalEnvios.toLocaleString('es-EC') },
            { label: 'Total $', value: fmt(totalDolares) },
            { label: 'Marketing', value: totalMarketing.toLocaleString('es-EC') },
            { label: 'Utilidad', value: totalUtilidad.toLocaleString('es-EC') },
          ].map(({ label, value }) => (
            <Card key={label} className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 via-50% to-transparent dark:from-emerald-800/20 dark:via-emerald-900/10 dark:via-50% dark:to-transparent">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                <p className="text-xl font-bold text-black dark:text-white">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConsumoMetaFiltersBar
        filters={filters}
        onFiltersChange={setFilters}
        paisesDisponibles={paisesDisponibles}
      />

      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-0">
          <ConsumoMetaTable
            registros={registros}
            isLoading={isLoading}
            anio={filters.anio ?? new Date().getFullYear()}
            mes={filters.mes ?? new Date().getMonth() + 1}
          />
        </CardContent>
      </Card>

      {isAdmin && (
        <AdminGestionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          record={null}
          onSubmit={handleSubmit}
          isSubmitting={createMutation.isPending}
        />
      )}
    </div>
  )
}
